import { useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react';

/**
 * Borde entre dos áreas de la consola que se jala para hacerlas más grandes o más chicas (como en
 * EasyWorship). Con el teclado, las flechas lo mueven; doble clic regresa al tamaño de siempre.
 * `measure` da el tamaño actual del área de antes (la de la izquierda o la de arriba).
 */
export function Splitter({ orientation, label, measure, onChange, onReset }: { orientation: 'vertical' | 'horizontal'; label: string; measure: (el: HTMLElement) => number; onChange: (size: number, el: HTMLElement) => void; onReset: () => void }) {
  const vertical = orientation === 'vertical';
  const [dragging, setDragging] = useState(false);

  function down(e: PointerEvent<HTMLDivElement>) {
    if (e.button !== 0) return;
    e.preventDefault();
    const el = e.currentTarget;
    const base = measure(el);
    const origin = vertical ? e.clientX : e.clientY;
    el.setPointerCapture(e.pointerId);
    setDragging(true);
    const move = (ev: globalThis.PointerEvent) => onChange(base + (vertical ? ev.clientX : ev.clientY) - origin, el);
    const up = () => {
      setDragging(false);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
  }

  function key(e: KeyboardEvent<HTMLDivElement>) {
    const step = e.shiftKey ? 64 : 16;
    const delta = { ArrowLeft: -step, ArrowRight: step, ArrowUp: -step, ArrowDown: step }[e.key];
    if (delta === undefined || (vertical ? e.key === 'ArrowUp' || e.key === 'ArrowDown' : e.key === 'ArrowLeft' || e.key === 'ArrowRight')) return;
    e.preventDefault();
    onChange(measure(e.currentTarget) + delta, e.currentTarget);
  }

  return (
    <div
      role="separator"
      aria-orientation={vertical ? 'vertical' : 'horizontal'}
      aria-label={label}
      title={`${label}: jala para cambiar el tamaño (doble clic: tamaño normal)`}
      tabIndex={0}
      className={`ewc__splitter ewc__splitter--${vertical ? 'v' : 'h'}${dragging ? ' ewc__splitter--drag' : ''}`}
      onPointerDown={down}
      onKeyDown={key}
      onDoubleClick={onReset}
    />
  );
}

type Sizes = { c1?: number; c2?: number; top?: number };
const KEY = 'keora.consola.tamanos';

/** Tamaños de las áreas de la consola que eligió el usuario, guardados en esta computadora. */
export function useConsoleSizes() {
  const [sizes, setSizes] = useState<Sizes>(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY) ?? '{}') as Sizes;
    } catch {
      return {};
    }
  });
  const set = (patch: Sizes) =>
    setSizes((prev) => {
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem(KEY, JSON.stringify(next));
      } catch {
        // Sin almacenamiento: solo no se recuerda.
      }
      return next;
    });

  const clamp = (n: number, min: number, max: number) => Math.round(Math.max(min, Math.min(max, n)));
  const prevSize = (el: HTMLElement, vertical: boolean) => {
    const r = el.previousElementSibling?.getBoundingClientRect();
    return r ? (vertical ? r.width : r.height) : 0;
  };
  const gridWidth = (el: HTMLElement) => el.parentElement?.getBoundingClientRect().width ?? 1000;
  const MIN = 140;

  /** Programa | diapositivas | Preview: cada columna deja al menos 140 px (Preview, 220). */
  const column = (which: 'c1' | 'c2') => ({
    measure: (el: HTMLElement) => prevSize(el, true),
    onChange: (size: number, el: HTMLElement) => {
      const other = which === 'c1' ? (sizes.c2 ?? prevSize(el.nextElementSibling?.nextElementSibling as HTMLElement, true)) : (sizes.c1 ?? prevSize(el.previousElementSibling?.previousElementSibling as HTMLElement, true));
      set({ [which]: clamp(size, MIN, gridWidth(el) - other - 220 - 24) });
    },
    onReset: () => set({ [which]: undefined }),
  });

  /** Arriba | biblioteca: arriba al menos 160 px y abajo al menos 150. */
  const top = {
    measure: (el: HTMLElement) => prevSize(el, false),
    onChange: (size: number, el: HTMLElement) => set({ top: clamp(size, 160, (el.parentElement?.getBoundingClientRect().height ?? 800) - 150 - 80) }),
    onReset: () => set({ top: undefined }),
  };

  const rootStyle = (sizes.top ? { '--ewc-top-h': `${sizes.top}px` } : undefined) as CSSProperties | undefined;
  const topStyle = {
    ...(sizes.c1 ? { '--ewc-c1': `min(${sizes.c1}px, 45%)` } : {}),
    ...(sizes.c2 ? { '--ewc-c2': `min(${sizes.c2}px, 55%)` } : {}),
  } as CSSProperties;
  return { column, top, rootStyle, topStyle };
}
